import type { NotificationListResponse } from '@esportsbudget/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, Check, Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useFormatters } from '@/lib/format';

/**
 * Колокольчик со счётчиком непрочитанных.
 *
 * Счётчик обновляется опросом раз в минуту. Постоянное соединение здесь
 * избыточно: напоминания приходят раз в день по расписанию, а не потоком,
 * и держать ради этого открытый канал на каждой вкладке незачем.
 */
export function NotificationBell() {
  const { t } = useTranslation(['notifications', 'common']);
  const format = useFormatters();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const countQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => api.get<NotificationListResponse>('/notifications', { page: 1, pageSize: 8 }),
    enabled: open,
  });

  const readMutation = useMutation({
    mutationFn: (ids?: string[]) => api.post('/notifications/read', ids ? { ids } : { all: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const unread = countQuery.data?.count ?? 0;
  const items = listQuery.data?.items ?? [];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={t('notifications:title')}
        title={t('notifications:title')}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-[10px] text-muted',
          'transition-colors duration-150 hover:bg-surface-2 hover:text-text',
          open && 'bg-surface-2 text-text',
        )}
      >
        <Bell className="h-[17px] w-[17px]" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-[340px] overflow-hidden rounded-[10px] border border-border bg-surface shadow-float animate-in">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
            <span className="text-[13.5px] font-semibold">{t('notifications:title')}</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readMutation.mutate(undefined)}
                className="text-[12px] text-accent hover:underline"
              >
                {t('notifications:markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {listQuery.isLoading ? (
              <p className="px-3.5 py-6 text-center text-[13px] text-subtle">
                {t('common:loading')}
              </p>
            ) : items.length === 0 ? (
              <div className="px-3.5 py-8 text-center">
                <Bell className="mx-auto mb-2 h-6 w-6 text-subtle" aria-hidden />
                <p className="text-[13px] text-muted">{t('notifications:empty')}</p>
                <p className="mt-1 text-[12px] text-subtle">{t('notifications:emptyHint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (!item.isRead) readMutation.mutate([item.id]);
                    }}
                    className={cn(
                      'flex w-full gap-2.5 px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-surface-2',
                      !item.isRead && 'bg-accent-soft/40',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg',
                        item.severity === 'DANGER'
                          ? 'bg-danger-soft text-danger'
                          : item.severity === 'WARN'
                            ? 'bg-warning-soft text-warning'
                            : 'bg-surface-2 text-muted',
                      )}
                    >
                      {item.severity === 'INFO' ? (
                        <Info className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium">{item.title}</span>
                      <span className="mt-0.5 block text-[12px] text-muted">{item.body}</span>
                      <span className="mt-1 block text-[11px] text-subtle">
                        {format.dateTime(item.createdAt)}
                        {item.workspaceName && ` · ${item.workspaceName}`}
                      </span>
                    </span>

                    {!item.isRead && (
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-border p-1.5">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              <Button variant="ghost" size="sm" fullWidth icon={<Check />}>
                {t('notifications:showAll')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
